import React from "react";
import styles from "./styles.scss";
import { BetQLService, BetQLSportItem } from "src/common-services";
import ProgressBar from "../../../GamesTable/GameMobileRow/ProgressBar";
import { COMP_GREEN, COMP_GRAY } from "../../../../colors";

function getPeriodFilter(filterKey: string) {
  const fgKeys = ["total", "spread", "moneyline"];
  return fgKeys.includes(filterKey) ? `FG${filterKey}` : filterKey;
}

function getPublicTitle(
  sport: BetQLSportItem,
  game: any,
  filterKey: string,
  atHome: "Home" | "Away" | "Draw",
  desktop: boolean
) {
  const { homeTeam, awayTeam } = game;
  if (filterKey.includes("total")) {
    return atHome === "Home" ? "Under" : "Over";
  } else if (sport.hasDraws && atHome === "Draw") {
    return "Draw";
  } else if (sport.hasDraws && !desktop) {
    return atHome === "Home" ? homeTeam.preferredAbbreviation : awayTeam.preferredAbbreviation;
  } else {
    return atHome === "Home" ? homeTeam.fullName : awayTeam.fullName;
  }
}

const TEAM_VALUES = ["Home", "Away", "Draw"];

const bestMoneyValue = (game: any, periodFilter: string) => {
  const percents = TEAM_VALUES.map((t) => game[`sharp${periodFilter}Public${t}Data`]).filter((v) => v);
  return Math.max(...percents);
};

const bestTicketValue = (game: any, periodFilter: string) => {
  const percents = TEAM_VALUES.map((t) => game[`sharp${periodFilter}Ticket${t}Data`]).filter((v) => v);
  return Math.max(...percents);
};

function SharpReportGraphs(props: { sport: BetQLSportItem; game: any; hasAccess: boolean; filterKey: string }) {
  const { game, hasAccess, filterKey, sport } = props;

  const periodFilter = getPeriodFilter(filterKey);
  const sportPeriodHasDraw = sport.hasDraws && periodFilter.includes("moneyline");

  let cols = [
    { team: "Away", align: "left" },
    { team: "Home", align: "right" },
  ];

  // hide draw UI if not moneyline or sport doesn't have draws
  if (sportPeriodHasDraw) {
    cols.splice(1, 0, { team: "Draw", align: "center" });
  }

  cols = cols.map(({ team, align }) => {
    const teamName = getPublicTitle(sport, game, filterKey, team as any, false);
    const fullTeamName = getPublicTitle(sport, game, filterKey, team as any, true);
    const barAlign = align === "right" ? "right" : "left";
    const moneyValue = hasAccess ? game[`sharp${periodFilter}Public${team}Data`] : 0;
    const ticketValue = hasAccess ? game[`sharp${periodFilter}Ticket${team}Data`] : 0;
    const moneyDisplay = game[`sharp${periodFilter}Public${team}Display`];
    const ticketDisplay = game[`sharp${periodFilter}Ticket${team}Display`];
    const hasMoneyDisplay = !hasAccess || moneyDisplay !== "--";
    const hasTicketDisplay = !hasAccess || ticketDisplay !== "--";
    const moneyColor = moneyValue === bestMoneyValue(game, periodFilter) ? COMP_GREEN : COMP_GRAY;
    const ticketColor = ticketValue === bestTicketValue(game, periodFilter) ? COMP_GREEN : COMP_GRAY;
    return {
      team,
      align,
      barAlign,
      sportPeriodHasDraw,
      teamName,
      fullTeamName,
      moneyValue,
      ticketValue,
      moneyDisplay,
      ticketDisplay,
      hasMoneyDisplay,
      hasTicketDisplay,
      moneyColor,
      ticketColor,
    };
  });

  return (
    <div className="sharp-report__flex-row">
      {cols.map((c: any, i: number) => (
        <div key={i} className="sharp-col sharp-report__flex-item">
          <div className="sharp-report__drawer-subtitle-text full-name">{c.fullTeamName}</div>
          <div className="sharp-report__drawer-subtitle-text short-name">{c.teamName}</div>

          <div className="sharp-report__progress-bar">
            <ProgressBar
              value={c.moneyValue}
              align="left"
              containerClassName="progress-bar__container"
              outerClassName={!hasAccess ? "progress-bar__outer progress-bar__outer--light" : "progress-bar__outer"}
              innerClassName="progress-bar__inner"
            />
            <div className={`sharp-report__drawer-text ${!hasAccess ? "sharp-report__drawer-text--dark" : ""}`}>
              % of Money {hasAccess ? (c.hasMoneyDisplay ? c.moneyDisplay : "Unavailable") : "--"}
            </div>
          </div>

          <div className="sharp-report__progress-bar">
            <ProgressBar
              value={c.ticketValue}
              align="left"
              containerClassName="progress-bar__container"
              outerClassName={!hasAccess ? "progress-bar__outer progress-bar__outer--light" : "progress-bar__outer"}
              innerClassName="progress-bar__inner"
            />

            <div className={`sharp-report__drawer-text ${!hasAccess ? "sharp-report__drawer-text--dark" : ""}`}>
              % of Tickets {hasAccess ? (c.hasTicketDisplay ? c.ticketDisplay : "Unavailable") : "--"}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export interface SharpBettorReportProps {
  sport: BetQLSportItem;
  game: any;
  filterKey: string;
  hasAccess: boolean;
  renderUpgradeButton: any;
}

function SharpReport(props: SharpBettorReportProps) {
  const { game, filterKey, hasAccess, renderUpgradeButton, sport } = props;
  const { homeTeam, awayTeam } = game;
  const periodFilter = getPeriodFilter(filterKey);
  const sharpDisplay = game[`sharp${periodFilter}Display`];
  const hasSharpDisplay = sharpDisplay !== "--";

  return (
    <div className="sharp-report sharp-report__container">
      <div className="sharp-report__drawer-title-text">Sharp Bettor Report</div>
      {!hasAccess ? (
        <div className="sharp-report__upgrade-notice">
          {`See if pro bettors like ${awayTeam.fullName} or ${homeTeam.fullName}`}
        </div>
      ) : null}
      <SharpReportGraphs sport={sport} game={game} hasAccess={hasAccess} filterKey={filterKey} />
      {hasAccess ? (
        <div className="sharp-report__pma-text">
          {hasSharpDisplay
            ? `${BetQLService.getSharpAbbrev(game, filterKey)} Pro Money Advantage by ${sharpDisplay}`
            : null}
        </div>
      ) : (
        <div className="sharp-report__upgrade-button">{renderUpgradeButton()}</div>
      )}
      <style jsx global>
        {styles}
      </style>
    </div>
  );
}

export default SharpReport;
